import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { GoogleMap, useJsApiLoader, Autocomplete } from '@react-google-maps/api';
import './MapPicker.css';

const containerStyle = {
  width: '100%',
  height: '260px',
  borderRadius: '12px',
  overflow: 'hidden',
  position: 'relative'
};

const defaultCenter = {
  lat: 42.6977, // Sofia
  lng: 23.3219
};

const LIBRARIES = ['places', 'geometry'];

// Helper: fix duplicated neighborhood tokens in Google Places formatted addresses (BG region bug)
const formatCleanAddress = (place) => {
  if (!place) return '';
  let address = place.formatted_address || place.name || '';
  
  // Regex: neighborhood name duplicated around a block number (Cyrillic or Latin)
  address = address.replace(/([А-Яа-яA-Za-z]{3,})\s+(\d+[А-Яа-яA-Za-z]?)\1/gi, '$1 $2');
  
  // Fix without number e.g. "VitoshaVitosha"
  address = address.replace(/([А-Яа-яA-Za-z]{4,})\1/gi, '$1');
  
  // Strip country for cleaner UI
  address = address.replace(/, България$/, '');
  address = address.replace(/, Bulgaria$/, '');
  
  return address;
};

const MapPicker = ({
  label,
  value,
  onChange,
  helperText,
  disabled,
  addressValue,
  onAddressChange
}) => {
  const [map, setMap] = useState(null);
  const [autocompleteAddress, setAutocompleteAddress] = useState(addressValue || '');
  const [isDragging, setIsDragging] = useState(false);
  const autocompleteRef = useRef(null);
  const geocoderRef = useRef(null);

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: apiKey,
    libraries: LIBRARIES
  });

  // Track map center state internally to prevent jumps from parent re-renders
  const [internalCenter, setInternalCenter] = useState(() => {
    if (value?.lat && value?.lng) {
      return { lat: Number(value.lat), lng: Number(value.lng) };
    }
    return defaultCenter;
  });

  // If the parent explicitly sets a fully NEW value (like when autocomplete outside is used)
  useEffect(() => {
    if (value?.lat && value?.lng && !isDragging && map) {
      const lat = Number(value.lat);
      const lng = Number(value.lng);
      const currentCenter = map.getCenter();
      
      if (!currentCenter) return;
      
      const cLat = Number(currentCenter.lat().toFixed(6));
      const cLng = Number(currentCenter.lng().toFixed(6));
      
      const newLat = Number(lat.toFixed(6));
      const newLng = Number(lng.toFixed(6));
      
      // Only recenter if it's significantly different (not just rounding)
      if (Math.abs(cLat - newLat) > 0.0001 || Math.abs(cLng - newLng) > 0.0001) {
        setInternalCenter({ lat, lng });
        map.panTo({ lat, lng });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, map]);

  useEffect(() => {
    if (isLoaded && window.google) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
  }, [isLoaded]);

  useEffect(() => {
    if (addressValue !== undefined && addressValue !== autocompleteAddress) {
      setAutocompleteAddress(addressValue);
    }
  }, [addressValue, autocompleteAddress]);

  const handlePlaceSelect = useCallback(() => {
    if (!autocompleteRef.current) return;

    const place = autocompleteRef.current.getPlace();
    if (!place.geometry || !place.geometry.location) return;

    const location = place.geometry.location;
    const newCoords = {
      lat: Number(location.lat().toFixed(6)),
      lng: Number(location.lng().toFixed(6))
    };

    if (onChange) onChange(newCoords);
    
    let address = formatCleanAddress(place);
    
    // Sometimes place doesn't have formatCleanAddress components if not queried properly, fallback:
    if(!address) address = place.formatted_address || place.name;

    setAutocompleteAddress(address);
    if (onAddressChange) onAddressChange(address);

    if (map) {
      setInternalCenter(newCoords);
      map.setCenter(location);
      map.setZoom(15);
    }
  }, [onChange, onAddressChange, map]);

  const handleDragStart = useCallback(() => {
    if (disabled) return;
    setIsDragging(true);
  }, [disabled]);

  const handleIdle = useCallback(() => {
    if (!map || disabled) return;
    
    // Only process if we were actually dragging (or just mounted)
    setIsDragging(false);
    
    const currentCenter = map.getCenter();
    if (!currentCenter) return;

    const lat = Number(currentCenter.lat().toFixed(6));
    const lng = Number(currentCenter.lng().toFixed(6));
    
    const newCoords = { lat, lng };

    // Update parent
    if (onChange) onChange(newCoords);

    // Geocode to get address for the new central point
    if (geocoderRef.current && onAddressChange) {
      geocoderRef.current.geocode({ location: currentCenter }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const address = formatCleanAddress(results[0]);
          setAutocompleteAddress(address);
          onAddressChange(address);
        }
      });
    }
  }, [map, disabled, onChange, onAddressChange]);

  const onLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const renderMap = () => {
    if (!apiKey) {
      return (
        <div className="map-placeholder error">
          <span>⚠️ Google Maps API key is missing.</span>
        </div>
      );
    }

    if (!isLoaded) {
      return (
        <div className="map-placeholder">
          <span>Loading map...</span>
        </div>
      );
    }

    if (loadError) {
      return (
        <div className="map-placeholder error">
          <span>❌ Map failed to load: {loadError.message}</span>
        </div>
      );
    }

    return (
      <div className={`map-container-interactive ${isDragging ? 'is-dragging' : ''}`} style={{ position: 'relative' }}>
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={internalCenter}
          zoom={value?.lat && value?.lng ? 15 : 12}
          onDragStart={handleDragStart}
          onIdle={handleIdle}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={{
            disableDefaultUI: false,
            zoomControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
            gestureHandling: disabled ? 'none' : 'greedy',
            clickableIcons: false,
            keyboardShortcuts: false,
            scrollwheel: true
          }}
        >
          {!disabled && (
            <div className="center-marker-overlay">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="#3b82f6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3" fill="white"></circle>
              </svg>
              <div className="marker-shadow"></div>
            </div>
          )}
          
          {disabled && value?.lat && value?.lng && (
            <div className="center-marker-overlay disabled">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="#64748b" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3" fill="white"></circle>
              </svg>
            </div>
          )}
        </GoogleMap>
      </div>
    );
  };

  return (
    <div className="map-picker">
      {label && <label className="map-picker-label">{label}</label>}
      
      {/* Address Autocomplete */}
      {isLoaded && !disabled && (
        <div className="map-autocomplete-wrapper">
          <Autocomplete
            onLoad={(autocomplete) => {
              autocompleteRef.current = autocomplete;
            }}
            onPlaceChanged={handlePlaceSelect}
            options={{
              componentRestrictions: { country: 'bg' },
              fields: ['geometry', 'formatted_address', 'name', 'address_components']
            }}
          >
            <input
              type="text"
              className="map-autocomplete-input"
              placeholder="Търси адрес или премести картата..."
              value={autocompleteAddress}
              onChange={(e) => {
                setAutocompleteAddress(e.target.value);
                if (onAddressChange) {
                  onAddressChange(e.target.value);
                }
              }}
              disabled={disabled}
            />
          </Autocomplete>
        </div>
      )}

      {!disabled && (
        <div className="map-instructions">
          <small>📍 Премести картата с пръст/мишка, за да нагласиш пина точно</small>
        </div>
      )}
      
      {renderMap()}
      
      {value?.lat && value?.lng && (
        <div className="map-coordinates">
          <span>Lat: {Number(value.lat).toFixed(6)}</span>
          <span>Lng: {Number(value.lng).toFixed(6)}</span>
        </div>
      )}
      
      {helperText && <small className="map-helper">{helperText}</small>}
    </div>
  );
};

export default React.memo(MapPicker);

